import PMFile from './pmFiles';
import { exampleDir, exampleSitesLatLon } from './fileConstants';
import { IDirData, IPmdData, ISitesData } from '../GlobalTypes';
import { ParseResult } from './validation';

const wrapPlain = <T>(data: T): ParseResult<T> => ({
  data,
  validation: { invalidRows: [] },
});

export const getDirectionalData = (file: File, as: string) => {
  const ext = /[.]/.exec(file.name)
    ? /[^.]+$/.exec(file.name)?.toString().toLowerCase()
    : undefined;

  return new Promise<ParseResult<IPmdData | IDirData>>((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      const handleRawData = (rawData: string | ArrayBuffer | null) => {
        const pmFile = new PMFile(
          file.name,
          file.type,
          file.size,
          file.webkitRelativePath,
          rawData,
        );
        switch (ext) {
          case 'pmd':
            return pmFile.parsePMD();
          case 'squid':
            return pmFile.parseSQUID();
          case 'rs3':
            return pmFile.parseRS3();
          case 'dir':
            return pmFile.parseDIR();
          case 'mdir':
            return pmFile.parseMDIR();
          case 'pmm':
            return pmFile.parsePMM();
          case 'csv': {
            if (as === 'pmd') return pmFile.parseCSV_PMD();
            if (as === 'dir') return pmFile.parseCSV_DIR();
            return wrapPlain(exampleDir);
          }
          case 'xlsx': {
            if (as === 'pmd') return pmFile.parseXLSX_PMD();
            if (as === 'dir') return pmFile.parseXLSX_DIR();
            return wrapPlain(exampleDir);
          }
          default:
            return wrapPlain(exampleDir);
        }
      };

      try {
        // Guard against empty text files to avoid downstream parser crashes
        if (ext !== 'xlsx') {
          const raw = reader.result;
          if (raw === null || (typeof raw === 'string' && raw.trim().length === 0)) {
            throw new Error(`Empty file: ${file.name}`);
          }
        }

        const parsed = handleRawData(reader.result);
        resolve(parsed);
      } catch (err) {
        reject(err);
      }
    };

    reader.onerror = reject;

    ext === 'xlsx' ? reader.readAsArrayBuffer(file) : reader.readAsText(file);
  });
};

export const getSitesLatLonData = (file: File) => {
  const ext = /[.]/.exec(file.name)
    ? /[^.]+$/.exec(file.name)?.toString().toLowerCase()
    : undefined;

  return new Promise<ISitesData>((resolve, reject) => {
    const reader = new FileReader();

    reader.onload = () => {
      const handleRawData = (rawData: string | ArrayBuffer | null) => {
        const pmFile = new PMFile(
          file.name,
          file.type,
          file.size,
          file.webkitRelativePath,
          rawData,
        );
        switch (ext) {
          case 'csv':
            return pmFile.parseCSV_SitesLatLon();
          case 'xlsx':
            return pmFile.parseXLSX_SitesLatLon();
          default:
            return exampleSitesLatLon;
        }
      };

      resolve(handleRawData(reader.result));
    };

    reader.onerror = reject;

    ext === 'xlsx' ? reader.readAsArrayBuffer(file) : reader.readAsText(file);
  });
};

export const s2ab = (s: string) => {
  const buf = new ArrayBuffer(s.length); // Convert s to arrayBuffer
  const view = new Uint8Array(buf); // Create uint8array as viewer
  for (let i = 0; i < s.length; i++) view[i] = s.charCodeAt(i) & 0xff; // Convert to octet - excel can read only octet (2^8) data
  return buf;
};

// Function to download data to a file
export const download = (data: string | ArrayBuffer, filename: string, type: string) => {
  const file = new Blob([data], { type: type });
  const a = document.createElement('a');
  const url = URL.createObjectURL(file);
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  setTimeout(function () {
    document.body.removeChild(a);
    window.URL.revokeObjectURL(url);
  }, 0);
};
